import { AfterViewInit, Component, ElementRef, ErrorHandler, Input, OnInit, ViewChild } from '@angular/core';
import { AviationEvent } from '../../shared/models/aviation-event.model';
import { GoogleMap } from '@agm/core/services/google-maps-types';
import { AgmMap } from '@agm/core';
import { QuickNavToc } from '../../shared/models/quick-nav-toc.model';
import { QuickNavComponent } from '../../shared/quick-nav/quick-nav.component';
import { EventLocationComponent } from '../event-location/event-location.component';
import { ReviewReportOffensiveComponent } from '../../shared/review-report-offensive/review-report-offensive.component';
import { State } from '../../shared/state-view/state.model';
import { MatDialog, MatDialogConfig, MatDialogRef, MatSnackBar } from '@angular/material';
import { AuthService } from '../../auth/auth/auth.service';
import { UserService } from '../../shared/user/user.service';
import { Router } from '@angular/router';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { EditEventImageComponent } from '../edit-event-image/edit-event-image.component';
import { DeleteEventImageComponent } from '../delete-event-image/delete-event-image.component';
import { DeleteEventComponent } from '../delete-event/delete-event.component';
import { CloneEventComponent } from '../clone-event/clone-event.component';
import { NgxGalleryImage, NgxGalleryImageSize, NgxGalleryOptions } from 'ngx-gallery';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';


@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.scss']
})
export class EventDetailComponent implements OnInit, AfterViewInit {
  @Input() aviationEvent: AviationEvent;
  state = State.Empty;
  isEventOwner = false;
  editModeSet = false;
  isFlightDeckAdmin = false;
  galleryOptions: NgxGalleryOptions[];
  galleryImages: NgxGalleryImage[];

  @ViewChild('map') map: AgmMap;
  @ViewChild('quicknav') quicknav: QuickNavComponent;
  @ViewChild('body', { read: ElementRef }) body: ElementRef;
  @ViewChild(EventLocationComponent) eventLocationSection: EventLocationComponent;

  private _nativeMap: GoogleMap;

  toc: QuickNavToc = new QuickNavToc('fbo-quicknav', [{
    label: 'Description',
    fragment: 'description',
    visible: true
  }, {
    label: 'Venue/Location',
    fragment: 'location',
    visible: true
  }, {
    label: 'Event Details',
    fragment: 'details',
    visible: true
  }, {
    label: 'Contact Info',
    fragment: 'contact',
    visible: true
  }, {
    label: 'Travel Discounts',
    fragment: 'travel',
    visible: true
  }]);

  constructor(public dialog: MatDialog,
              private _auth: AuthService,
              private _userService: UserService,
              private _errorHandler: ErrorHandler,
              private _snackBar: MatSnackBar,
              private _router: Router,
              private _aviationEventsService: AviationEventsService,
              private businessManagerService: BusinessManagerService,
              private _toggleService: EditToggleService,
              private _analyticsService: AnalyticsService) { }

  async ngOnInit() {
    this.isEventOwner = await this.businessManagerService.userOwnsEvent(this.aviationEvent.recid);
    this.isFlightDeckAdmin = await this.businessManagerService.isFlightDeckAdmin();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });

    this.galleryOptions = [
      {
        width: '391px',
        height: '489px',
        thumbnailsColumns: 3,
        image: true,
        imageSize: 'contain',
        preview: true,
        previewCloseOnClick: true,
        previewCloseOnEsc: true,
        thumbnailsMargin: 5,
        thumbnailMargin: 5,
        imageArrows: true,
        imageArrowsAutoHide: false,
        previewArrows: true,
        thumbnailsArrowsAutoHide: true
      }
    ];

    if (this.aviationEvent.galleryPhotos) {
      this.galleryImages = this.aviationEvent.galleryPhotos;
      if (this.aviationEvent.galleryPhotos.length === 1) {
        this.galleryOptions = [
          {
            width: '391px',
            height: '489px',
            thumbnailsColumns: 3,
            image: true,
            imageSize: 'contain',
            preview: true,
            previewCloseOnClick: true,
            previewCloseOnEsc: true,
            thumbnailsMargin: 5,
            thumbnailMargin: 5,
            imageArrows: false,
            imageArrowsAutoHide: false,
            previewArrows: false,
            thumbnailsArrowsAutoHide: true
          }
        ];
      }
    } else {
      this.galleryImages = [];
    }
    this._analyticsService.pageView('airport_event', 'Event Detail');
    this._analyticsService.trackEvent('airport_event_viewed', this.aviationEvent.eventId.toString(), this.aviationEvent.eventId.toString());
    if (this._auth.authenticated && this._auth.endUser) {
      this._analyticsService.trackEvent('airport_event_loggedin', this._auth.recid, this._auth.recid);
    } else {
      this._analyticsService.trackEvent('airport_event_loggedout', '', '');
    }
  }

  async ngAfterViewInit() {
    if (this.aviationEvent.location) {
      const nativeMap = await this._initMap();
      nativeMap.setCenter({ lat: this.aviationEvent.location.lat, lng: this.aviationEvent.location.lon });
    }
  }

  private async _initMap(): Promise<GoogleMap> {
    return new Promise<GoogleMap>((resolve, reject) => {
      this.map.mapReady.subscribe(
        map => {
          this._nativeMap = map;
          resolve(map);
        },
        err => reject(err)
      );
    });
  }

  get directionsURL(): string {
    return this.aviationEvent.zipcode ? this.aviationEvent.address1 + '+' + this.aviationEvent.city + '+' +
      this.aviationEvent.stateProvince + '+' + this.aviationEvent.zipcode
      : this.aviationEvent.address1 + '+' + this.aviationEvent.city + '+' +
      this.aviationEvent.stateProvince;
  }

  dataChanged(aviationEvent: AviationEvent) {
    this.aviationEvent = aviationEvent;
  }

  async reloadEntity() {
    this.aviationEvent = await this._aviationEventsService.getAviationEventById(this.aviationEvent.eventId);
  }

  async reportOffensive() {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const dialogRef = this.dialog.open(ReviewReportOffensiveComponent, {
      width: '550px',
      data: {
        offensiveType: 'event'
      }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        // if user confirmed delete
        const snackBarOpts = {duration: 2000};
        try {
          const response = await this._userService.setAviationEventInteraction(this.aviationEvent.eventId, 'Offensive');
          this.state = State.Content;
          this.reloadEntity();
          this._snackBar.open('Thank you.  Your report has been logged and will be reviewed as soon as possible.',
            'Dismiss', {duration: 6000});
        } catch (e) {
          this._snackBar.open('An error occurred saving report. Please try again later.', 'Dismiss', snackBarOpts);
          this._errorHandler.handleError(e);

          // Set state to error, then reset after a few seconds to allow retry.
          this.state = State.Error;
          setTimeout(() => {
            this.state = State.Content;
          }, 2000);
        }
      }
    });
  }

  async removeOffensive() {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const snackBarOpts = {duration: 2000};
    try {
      const response = await this._userService.setAviationEventInteraction(this.aviationEvent.eventId, 'ClearOffensive');
      this.state = State.Content;
      this.reloadEntity();
    } catch (e) {
      this._snackBar.open('An error occurred removing your report. Please try again later.', 'Dismiss', snackBarOpts);
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }

  async clearAllOffensive() {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const snackBarOpts = {duration: 2000};
    try {
      const response = await this._userService.setAviationEventInteraction(this.aviationEvent.eventId, 'NotOffensive');
      this.state = State.Content;
      this.reloadEntity();
    } catch (e) {
      this._snackBar.open('An error occurred removing your report. Please try again later.', 'Dismiss', snackBarOpts);
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }

  openEditImageDialog() {
    const DATA = {aviationEvent: this.aviationEvent};
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = DATA;

    const DIALOG_REF = this.dialog.open(EditEventImageComponent, dialogConfig);
    this._handleModalClose(DIALOG_REF);
  }

  /**
   * listens for dialog close event. Opens snackbar if necessary
   * @param {MatDialogRef<any>} dialogRef
   * @param {string} successMessage
   * @param {string} actionMessage
   * @private
   */
  _handleModalClose (dialogRef: MatDialogRef<any>, successMessage = 'Event Cover Photo/Image', actionMessage = 'Updated') {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';

    dialogRef.afterClosed().subscribe((success) => {
      if (success === null) { return; }
      if (success.cancelBtnClicked) { return; }
      if (success) {
        if (successMessage === 'Aviation Event') {
          this._router.navigate(['/'], { queryParams: { 't': 'events' } });
        } else {
          this.reloadEntity();
        }
        this._snackBar.open(successMessage, actionMessage, {
          duration: 5000
        });
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openDeleteImageDialog() {
    const DATA = {aviationEvent: this.aviationEvent};
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = DATA;

    const DIALOG_REF = this.dialog.open(DeleteEventImageComponent, dialogConfig);
    this._handleModalClose(DIALOG_REF, 'Event Cover Photo/Image', 'Deleted');
  }

  openDeleteEventDialog() {
    const DATA = {aviationEvent: this.aviationEvent};
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = DATA;

    const DIALOG_REF = this.dialog.open(DeleteEventComponent, dialogConfig);
    this._handleModalClose(DIALOG_REF, 'Aviation Event', 'Deleted');
  }

  openCloneEventDialog() {
    const DATA = {aviationEvent: this.aviationEvent};
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = DATA;

    const DIALOG_REF = this.dialog.open(CloneEventComponent, dialogConfig);
    this._handleModalClose(DIALOG_REF, 'Event Clone Created', 'And Currently Showing');
  }
}
